import { Users, Music, SlidersHorizontal, MapPin, Heart, Sparkles, Volume2, Disc3 } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { AREAS, IMAGES } from "../../lib/site";

const WHY = [
  { icon: Users, title: "A Wealth of Talent", text: "A diverse group of male and female singers — trained vocalists among them — each bringing a different voice and style to the stage." },
  { icon: Music, title: "Classics That Connect", text: "A strong repertoire of retro Bollywood and English classics that every generation in the room can sing along to." },
  { icon: SlidersHorizontal, title: "Flexible Performances", text: "The number of singers, sets and format adapts to your event — from an intimate gathering to a full evening concert." },
  { icon: MapPin, title: "Tricity-Based", text: "Available across Chandigarh, Mohali, Panchkula, Zirakpur, Kharar and New Chandigarh." },
  { icon: Sparkles, title: "More Than Just a Performance", text: "Private celebrations, corporate gatherings, cafés, societies and concerts — one ensemble, many moods." },
  { icon: Heart, title: "Passion With Experience", text: "Performers who sing because they genuinely love music — and it shows in every note." },
];

export function About() {
  return (
    <section id="about" className="relative py-20 lg:py-28" data-testid="about-section">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
        <div>
          <SectionHead align="left" eyebrow="Our Story" title="Passion Doesn't Retire." />
          <Reveal delay={0.1}>
            <p className="mt-6 text-sm leading-relaxed text-slate-300 sm:text-base">
              Tricity Kroonerz began as a gathering of like-minded music enthusiasts — people who
              simply could not stop singing. Today, it has grown into an ensemble of around sixteen
              active members, retired senior citizens who continue to pursue their love of music
              with the energy of performers half their age.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-slate-300 sm:text-base">
              Among the group are trained singers, male and female vocalists, and a shared devotion
              to the golden era — retro Bollywood melodies and English classics, performed live over
              karaoke backing tracks. Their music has filled rooms at DSOI Sector 36, the Chandigarh
              Golf Club, elite private gatherings, old age homes and senior citizen meets.
            </p>
            <blockquote className="mt-8 border-l-2 border-gold pl-5 font-serif text-xl italic leading-snug text-gold-light" data-testid="about-quote">
              They retired from their careers. They did not retire from their passion for music.
            </blockquote>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="relative">
          <div className="grid grid-cols-2 gap-4">
            <div className="gold-frame overflow-hidden rounded-2xl border border-gold/20">
              <img src={IMAGES.vinyl} alt="Vinyl record grooves in amber light" loading="lazy" className="h-64 w-full object-cover sm:h-80" />
            </div>
            <div className="mt-10 overflow-hidden rounded-2xl border border-navy-700">
              <img src={IMAGES.hall} alt="Warm acoustic hall ambience" loading="lazy" className="h-64 w-full object-cover sm:h-80" />
            </div>
          </div>
          <div className="absolute -bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full border border-navy-700 bg-navy-900/95 px-5 py-2.5 backdrop-blur">
            <Disc3 className="h-4 w-4 text-gold" />
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-300">Hindi & English · Retro Classics</span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export function Why() {
  return (
    <section className="relative bg-navy-900/50 py-20 lg:py-28" data-testid="why-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHead
          eyebrow="Why Tricity Kroonerz"
          title="Experience You Can Hear"
          sub="Not a band for hire off a listing site — a community of seasoned singers who have spent a lifetime loving this music."
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={0.06 * i}>
              <div className="tk-card group h-full p-7 transition-all duration-300 hover:-translate-y-1 hover:border-gold/40" data-testid={`why-card-${i}`}>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-burgundy/25 text-gold transition-colors group-hover:bg-gold group-hover:text-navy-950">
                  <w.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 font-serif text-xl font-semibold text-ivory">{w.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-400">{w.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  { n: "01", title: "Tell Us About Your Event", text: "Share your event type, date, location, expected audience and requirements." },
  { n: "02", title: "Discuss Your Performance", text: "We talk through the number of singers, duration, songs and overall format together." },
  { n: "03", title: "Finalize Requirements", text: "Sound, additional musicians, refreshments and other details are agreed upon." },
  { n: "04", title: "Let the Music Begin", text: "Tricity Kroonerz takes the stage — and your evening becomes a memory." },
];

export function Process() {
  return (
    <section className="py-20 lg:py-28" data-testid="process-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <SectionHead eyebrow="How Booking Works" title="Four Steps to a Musical Evening" sub="A simple, personal process — no portals, no packages, no surprises." />
        <div className="mt-16 grid gap-10 md:grid-cols-4 md:gap-6">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={0.08 * i} className="relative">
              {i < 3 && <div className="absolute left-[26px] top-[52px] hidden h-px w-[calc(100%+24px)] bg-gradient-to-r from-gold/50 to-navy-700 md:block" />}
              <div className="relative z-10 flex h-[52px] w-[52px] items-center justify-center rounded-full border border-gold/50 bg-navy-900 font-serif text-lg font-bold text-gold" data-testid={`process-step-${i + 1}`}>
                {s.n}
              </div>
              <h3 className="mt-5 font-serif text-lg font-semibold text-ivory">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.text}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SoundSupport() {
  return (
    <section className="relative overflow-hidden bg-navy-900/50 py-20 lg:py-28" data-testid="sound-section">
      <div className="spotlight-gold pointer-events-none absolute right-[-15%] top-[-20%] h-[480px] w-[480px]" />
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2">
        <Reveal>
          <div className="gold-frame overflow-hidden rounded-2xl border border-gold/20">
            <img src={IMAGES.guitars} alt="Classic instruments resting in warm ambient light" loading="lazy" className="h-80 w-full object-cover sm:h-[420px]" />
          </div>
        </Reveal>
        <div>
          <SectionHead align="left" eyebrow="Sound & Event Support" title="From Intimate Gatherings to Larger Shows" />
          <Reveal delay={0.1}>
            <ul className="mt-8 space-y-5">
              {[
                "For smaller events, we bring our own microphones, speakers and amplifiers — a complete, intimate setup included with the performance.",
                "For larger events needing professional sound, we can work with your sound provider — or help arrange external equipment at an additional cost.",
                "For larger concerts, sound, refreshments and other requirements can be coordinated according to the scale of the event.",
              ].map((t, i) => (
                <li key={i} className="flex gap-4">
                  <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold/10 text-gold">
                    <Volume2 className="h-4 w-4" />
                  </span>
                  <p className="text-sm leading-relaxed text-slate-300">{t}</p>
                </li>
              ))}
            </ul>
            <p className="mt-8 rounded-xl border border-navy-700 bg-navy-850/70 p-4 text-xs leading-relaxed text-slate-400">
              Transparent by design: external sound, refreshments, travel and other third-party
              requirements may involve additional charges — always mutually agreed before the event.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export function Venues({ venues }) {
  return (
    <section className="py-20 lg:py-24" data-testid="venues-section">
      <div className="mx-auto max-w-5xl px-4 text-center sm:px-6">
        <SectionHead eyebrow="Where We've Performed" title="Stages We Know Well" />
        <div className="mt-10 flex flex-wrap justify-center gap-3">
          {venues.map((v, i) => (
            <Reveal key={v.name} delay={0.05 * i}>
              <span className="inline-flex items-center gap-2 rounded-full border border-gold/25 bg-navy-850/80 px-5 py-2.5 text-sm font-semibold text-ivory" data-testid={`venue-chip-${i}`}>
                <MapPin className="h-3.5 w-3.5 text-gold" /> {v.name}
              </span>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-8 text-sm text-slate-400">
            And at homes, clubs, societies and community gatherings across {AREAS.join(", ")}.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
